/**
 * ErrorBoundary — React error boundary with fallback UI
 *
 * Catches render errors in child components and shows a user-friendly error message.
 * - Retry count protection: max 3 retries before showing permanent error
 * - Chunk load failure detection: auto-reload page for lazy() import failures
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

const MAX_RETRIES = 3;

/** Detect chunk/module load failures from React.lazy dynamic imports */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message.toLowerCase();
  return msg.includes('loading chunk') ||
    msg.includes('dynamically imported module') ||
    msg.includes('failed to fetch');
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, retryCount: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info);
  }

  private handleRetry = () => {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      // Chunk load failure — reload page to get fresh assets
      window.location.reload();
      return;
    }
    this.setState((prev) => ({
      hasError: false,
      error: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const isChunkError = this.state.error && isChunkLoadError(this.state.error);
      const canRetry = isChunkError || this.state.retryCount < MAX_RETRIES;

      return (
        <div className="flex h-full min-h-[200px] items-center justify-center p-8">
          <div className="max-w-md text-center space-y-3">
            <p className="text-3xl">⚠️</p>
            <h3 className="text-lg font-semibold text-destructive">出了点问题</h3>
            <p className="text-sm text-muted-foreground">
              {isChunkError
                ? '页面资源加载失败，请刷新重试'
                : (this.state.error?.message ?? '未知错误')}
            </p>
            {canRetry ? (
              <button type="button"
                onClick={this.handleRetry}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                {isChunkError ? '刷新页面' : '重试'}
              </button>
            ) : (
              <p className="text-xs text-muted-foreground">
                多次重试仍然失败，请刷新整个页面
              </p>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}